import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FlaskConical, Plus, Send, Trash2, Loader2, Radar, ShieldCheck, Mail, MailSearch, Brain, Handshake, Bot, Clock, UserCheck,
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, MessageSquare, Save, RotateCcw, Hourglass, Info, XCircle
} from 'lucide-react';
import { api, inputCls, Button, Section, Field, Notice, StatusBadge, AGENT_LABEL, KIND_LABEL, describeTerms, timeAgo, formatWhen, tierLabel } from './ui';
import { StepIcon } from './Network';
import { Bubble, TRAFFIC_LABEL } from './Deals';
import { BRAND_KINDS } from './TeamSetup';

/**
 * Sandbox: you play an affiliate. Pick the deal (or write one with the deal
 * desk), the brand that approaches you and the site you run; the real agents
 * then work on you as they would on a live affiliate. Nothing is sent, and
 * every email says what live mode would have done with it.
 */

const AGENT_ICON = { scout: Radar, vetter: ShieldCheck, outreach: Mail, classifier: MailSearch, strategist: Brain, negotiator: Handshake, stopper: Hourglass };

// Replies that exercise each route: terms, a counter, a traffic claim, a rival, PPC, a call, a referral, away, a no, an opt-out.
const TRY_REPLIES = [
  'Thanks for reaching out. What deal can you offer?',
  'We only work on 50% RevShare with new brands.',
  'We get 400,000 UK visits a month, so we need a much better rate than that.',
  'Your traffic data is wrong. We are an SEO site, not PPC.',
  'Another casino pays us 45% RevShare. Can you match it?',
  'We run Google Ads for casino keywords in the UK, so we need a higher CPA than SEO sites. What can you do?',
  'Sounds good. Can we move this to Telegram?',
  'Please talk to my colleague Jane: jane@{domain}',
  'I am out of the office until Monday.',
  'Not interested, thanks.',
  'Please remove me from your list.'
];

const VERDICT = {
  approve: { label: 'Approve', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  review: { label: 'Review', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  reject: { label: 'Reject', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' }
};

// The Negotiator's moves, in plain words.
const MOVE_LABEL = {
  answer: 'Answer the question',
  qualify: 'Ask before naming a number',
  hold: 'Hold the offer',
  trade: 'Give only for something back',
  step: 'A small step',
  ask_proof: 'Ask for their own numbers',
  close: 'Close',
  hand_over: 'Hand to a person',
  decline: 'Decline kindly'
};

const TONE = {
  info: { Icon: Info, cls: 'text-gray-600 dark:text-slate-400' },
  ok: { Icon: CheckCircle2, cls: 'text-green-700 dark:text-green-400' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-700 dark:text-amber-300' },
  error: { Icon: XCircle, cls: 'text-red-700 dark:text-red-400' }
};

const ENDED = ['won', 'opted_out'];

// Web search citations arrive as markdown links: show the source name only.
const plain = (s) => String(s || '').replace(/\s*\(?\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\)?/g, ' ($1)').trim();

const duration = (start, end) => {
  if (!start) return null;
  const s = Math.max(0, Math.round(((end ? new Date(end) : new Date()).getTime() - new Date(start).getTime()) / 1000));
  if (Number.isNaN(s)) return null;
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const SandboxStatus = ({ status }) => (status === 'ready'
  ? <span className="inline-flex whitespace-nowrap rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">Starting</span>
  : <StatusBadge status={status} />);

/* ---- the conversation ---- */

/** One agent run: a one-line summary, open for its steps. Open and live while it is running. */
const RunRow = ({ run, live }) => {
  const [open, setOpen] = useState(false);
  const Icon = AGENT_ICON[run.agent] || Bot;
  const steps = run.steps || [];
  const shown = live || open;
  const took = duration(run.started_at, run.finished_at);
  return (
    <div className={`mx-auto w-full max-w-2xl rounded-lg border text-xs ${live
      ? 'border-blue-300 bg-white dark:border-blue-800 dark:bg-slate-900'
      : run.status === 'failed' ? 'border-red-200 bg-white dark:border-red-900 dark:bg-slate-900' : 'border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900'}`}>
      <button type="button" onClick={() => setOpen(!open)} disabled={live} className="flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left">
        {live ? <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-blue-600 dark:text-blue-400" />
          : open ? <ChevronDown className="h-3.5 w-3.5 flex-none text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 flex-none text-gray-400" />}
        <Icon className="h-3.5 w-3.5 flex-none text-gray-600 dark:text-slate-300" />
        <span className="flex-none font-medium text-gray-900 dark:text-slate-100">{AGENT_LABEL[run.agent] || run.agent}</span>
        <span className="min-w-0 truncate text-gray-500 dark:text-slate-400" title={run.summary || run.title}>{live ? `${run.title}...` : run.summary || run.title}</span>
        <span className="ml-auto hidden flex-none items-center gap-0.5 sm:flex">
          {steps.filter((s) => s.state !== 'skip').map((s) => <StepIcon key={s.key} state={s.state} still={!live} />)}
        </span>
        {tierLabel(run.model) && <span className="hidden flex-none rounded-full bg-gray-100 px-1.5 text-[10px] text-gray-600 dark:bg-slate-800 dark:text-slate-300 md:inline">{tierLabel(run.model)}</span>}
        {took && <span className="flex-none text-gray-400 dark:text-slate-500">{took}</span>}
      </button>
      {shown && (
        <ul className="grid gap-x-4 gap-y-1 border-t border-gray-100 px-3 py-2 dark:border-slate-800 sm:grid-cols-2">
          {steps.map((s) => (
            <li key={s.key} className={`flex min-w-0 items-start gap-1.5 ${s.state === 'skip' || s.state === 'wait' ? 'text-gray-400 dark:text-slate-500' : 'text-gray-700 dark:text-slate-300'}`}>
              <span className="mt-px"><StepIcon state={s.state} still={!live} /></span>
              <span className="min-w-0"><span className="font-medium">{s.label}</span>{s.note ? `: ${s.note}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FitBar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[11px] text-gray-500 dark:text-slate-400"><span>{label}</span><span>{value ?? 'n/a'}</span></div>
    <div className="h-1.5 rounded-full bg-gray-200 dark:bg-slate-800">
      <div className="h-1.5 rounded-full bg-blue-600 dark:bg-blue-400" style={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%` }} />
    </div>
  </div>
);

const VetCard = ({ m, marketName }) => {
  const v = VERDICT[m.verdict] || VERDICT.review;
  const source = (s) => (!s || s === 'none' ? 'not in MARS' : s);
  return (
    <div className="mx-auto w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-3 text-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-gray-600 dark:text-slate-300" />
        <span className="font-medium text-gray-900 dark:text-slate-100">Vetter's verdict</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}>{v.label}</span>
        <span className="text-xs text-gray-500 dark:text-slate-400">score {m.score}, {m.value} value</span>
      </div>
      {m.site_summary && <p className="mt-1 text-gray-600 dark:text-slate-400">{m.site_summary}</p>}
      <div className="mt-2 grid grid-cols-3 gap-3">
        <FitBar label="Markets" value={m.fit?.markets} />
        <FitBar label="Keywords" value={m.fit?.keywords} />
        <FitBar label="Site" value={m.fit?.site} />
      </div>
      {m.reasons?.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-gray-700 dark:text-slate-300">{m.reasons.map((r) => <li key={r}>{plain(r)}</li>)}</ul>
      )}
      {m.red_flags?.length > 0 && <div className="mt-1 text-xs text-red-700 dark:text-red-400">Red flags: {m.red_flags.map(plain).join('; ')}</div>}
      {m.traffic && (
        <div className={`mt-1 text-xs ${m.bids_on_brand ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}`}>
          <span className="font-medium">Traffic:</span> {m.traffic}
        </div>
      )}
      {m.rule && <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Rule: {m.rule}</div>}
      {m.hook && <div className="mt-2 text-xs text-gray-700 dark:text-slate-300"><span className="font-medium">Hook for the first email:</span> {m.hook}</div>}
      <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
        Lead market: {m.recommended_geo ? marketName(m.recommended_geo) : 'not set'}
        {m.top_markets?.length > 0 && ` · top GEOs: ${m.top_markets.slice(0, 4).map((x) => x.market).join(', ')}`}
        {' '}· GEO data: {source(m.sources?.geos)} · keyword data: {source(m.sources?.keywords)}
      </div>
      <div className="mt-2 text-xs font-medium text-gray-800 dark:text-slate-200">{m.live}</div>
    </div>
  );
};

const kindLabel = (m) => {
  if (m.kind === 'followup') return m.nudge ? 'Nudge' : `Follow-up ${m.step} of ${m.total}`;
  if (m.kind === 'first_touch' && m.with_offer) return 'First email, with the deal';
  return KIND_LABEL[m.kind] || m.kind;
};

/** An email from the team, as you would receive it, and what live mode would do with it. */
const EmailBubble = ({ m }) => {
  const [why, setWhy] = useState(false);
  const live = m.live || {};
  const issues = m.issues || [];
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[90%] rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900 sm:max-w-[80%]">
        <div className="flex flex-wrap items-center gap-x-2 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500 dark:border-slate-800 dark:text-slate-400">
          <span className="font-medium text-gray-800 dark:text-slate-200">{m.from_name || 'The team'}</span>
          {m.from && <span>&lt;{m.from}&gt;</span>}
          <span>to {m.to}</span>
          <span className="ml-auto">{AGENT_LABEL[m.agent] || m.agent} · {kindLabel(m)}</span>
        </div>
        {m.subject && <div className="px-3 pt-2 text-sm font-medium text-gray-900 dark:text-slate-100">{m.subject}</div>}
        <div className="whitespace-pre-wrap px-3 py-2 text-sm text-gray-800 dark:text-slate-200">{m.full || m.body}</div>
        {issues.length > 0 && (
          <div className="mx-3 mb-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            The code checks stopped it: {issues.join(' ')}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-3 py-1.5 text-xs dark:border-slate-800">
          {live.approval ? (
            <span className="flex items-start gap-1 text-amber-700 dark:text-amber-300">
              <UserCheck className="mt-px h-3.5 w-3.5 flex-none" />Live: a person approves it first.{issues.length ? '' : ` ${live.approval}`}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-gray-500 dark:text-slate-400">
              <Clock className="h-3.5 w-3.5 flex-none" />Live: {live.send_at ? `goes by itself, ${formatWhen(live.send_at)}` : live.due || 'goes by itself'}
            </span>
          )}
          {m.agent === 'negotiator' && (
            <button type="button" onClick={() => setWhy(!why)} className="ml-auto text-blue-700 hover:underline dark:text-blue-300">{why ? 'Hide' : 'Why this reply'}</button>
          )}
        </div>
        {why && (
          <dl className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1 border-t border-gray-100 bg-gray-50 px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-950">
            <dt className="col-span-2 font-medium text-gray-700 dark:text-slate-300">The Negotiator's plan</dt>
            <dt className="text-gray-500 dark:text-slate-400">Stage</dt><dd className="text-gray-800 dark:text-slate-200">{String(m.stage || '').replace(/_/g, ' ')}</dd>
            {m.move && <><dt className="text-gray-500 dark:text-slate-400">Move</dt><dd className="text-gray-800 dark:text-slate-200">{MOVE_LABEL[m.move] || m.move}</dd></>}
            <dt className="text-gray-500 dark:text-slate-400">What you want</dt><dd className="text-gray-800 dark:text-slate-200">{m.read}</dd>
            {(m.claims || []).length > 0 && (
              <>
                <dt className="text-gray-500 dark:text-slate-400">Claims checked</dt>
                <dd className="space-y-0.5 text-gray-800 dark:text-slate-200">
                  {m.claims.map((c) => <div key={c.claim}><span className="font-medium">{c.claim}:</span> {c.finding}</div>)}
                </dd>
              </>
            )}
            <dt className="text-gray-500 dark:text-slate-400">Your ask</dt><dd className="text-gray-800 dark:text-slate-200">{describeTerms(m.their_ask, m.currency) || 'Nothing named yet'}</dd>
            <dt className="text-gray-500 dark:text-slate-400">Its offer</dt><dd className="text-gray-800 dark:text-slate-200">{describeTerms(m.our_offer, m.currency) || 'None in this email'}</dd>
            <dt className="text-gray-500 dark:text-slate-400">Your position</dt><dd className="text-gray-800 dark:text-slate-200">{m.position ? `#${m.position}` : 'Not said yet'}</dd>
            {m.step && <><dt className="text-gray-500 dark:text-slate-400">Step</dt><dd className="text-gray-800 dark:text-slate-200">{m.step} (the last step is the budget)</dd></>}
            {m.ask_in_return && <><dt className="text-gray-500 dark:text-slate-400">Asks in return</dt><dd className="text-gray-800 dark:text-slate-200">{m.ask_in_return}</dd></>}
            <dt className="text-gray-500 dark:text-slate-400">Strategy</dt><dd className="text-gray-800 dark:text-slate-200">{m.strategy}</dd>
          </dl>
        )}
      </div>
    </div>
  );
};

const AffiliateBubble = ({ m }) => (
  <div className="flex justify-end">
    <div className="max-w-[90%] rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950/40 sm:max-w-[75%]">
      <div className="mb-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
        <span>You{m.from_name ? ` (${m.from_name})` : ''}</span>
        {m.intent && (
          <span className="rounded-full bg-white px-1.5 text-[11px] text-gray-600 dark:bg-slate-900 dark:text-slate-300">
            read as: {m.intent === 'unsubscribe' ? 'opt-out' : m.intent.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200">{m.body}</div>
    </div>
  </div>
);

const NoteRow = ({ m }) => {
  const t = TONE[m.tone] || TONE.info;
  return (
    <div className={`mx-auto flex w-full max-w-2xl items-start gap-1.5 px-1 text-xs ${t.cls}`}>
      <t.Icon className="mt-px h-3.5 w-3.5 flex-none" />
      <span>{m.text}</span>
    </div>
  );
};

const Entry = ({ m, marketName }) => {
  if (m.role === 'email') return <EmailBubble m={m} />;
  if (m.role === 'affiliate') return <AffiliateBubble m={m} />;
  if (m.role === 'run') return <RunRow run={m} />;
  if (m.role === 'vet') return <VetCard m={m} marketName={marketName} />;
  if (m.role === 'note') return <NoteRow m={m} />;
  return null;
};

const SandboxChat = ({ box, marketName, onChange, onDelete, onNew }) => {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const count = box.messages.length;
  const liveSteps = (box.live?.steps || []).map((s) => s.state).join();
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, liveSteps]);

  const act = async (kind) => {
    const message = input.trim();
    setSending(kind);
    setError(null);
    if (kind === 'reply') setInput('');
    try {
      const path = { reply: 'reply', no_reply: 'no-reply', start: 'start' }[kind];
      const d = await api(`/sandbox/${box.id}/${path}`, { method: 'POST', body: kind === 'reply' ? { message } : undefined });
      onChange(d.sandbox);
    } catch (err) {
      setError(err.message);
      if (kind === 'reply') setInput(message);
    } finally {
      setSending(null);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this sandbox chat?')) return;
    setDeleting(true);
    try {
      await onDelete(box.id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const contact = box.contact || {};
  const who = contact.name || contact.email;
  const ended = !box.busy && ENDED.includes(box.status);
  const canType = box.can?.reply && !sending;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <FlaskConical className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-gray-900 dark:text-slate-100">{box.brand_name || box.team_name} approaching {box.persona.domain}</span>
            <SandboxStatus status={box.status} />
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            You are {who}{contact.name ? ` <${contact.email}>` : ''}
            {' '}· Deal: {box.deal?.name || 'none set'}{box.deal?.opening_line ? ` (opens at ${box.deal.opening_line})` : ''}
            {box.persona.note ? ` · The team knows: ${box.persona.note}` : ''}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button icon={Plus} onClick={onNew}>New sandbox</Button>
          <Button variant="danger" icon={Trash2} busy={deleting} disabled={!!box.busy} onClick={remove}>Delete</Button>
        </div>
      </div>

      {box.status === 'needs_human' && !box.busy && (
        <Notice tone="warn">Live, a person takes over here: {box.human_reason} You can carry on as if they handed it back.</Notice>
      )}

      <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div ref={scrollRef} className="max-h-[65vh] space-y-3 overflow-y-auto rounded-t-xl bg-gray-50 p-4 dark:bg-slate-950">
          {box.messages.map((m) => <Entry key={m.id} m={m} marketName={marketName} />)}
          {box.live ? <RunRow run={box.live} live /> : box.busy ? (
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />The agents are starting...
            </div>
          ) : null}
        </div>
        {error && <div className="px-3 pt-3"><Notice tone="error">{error}</Notice></div>}

        {ended ? (
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 p-3 text-sm text-gray-600 dark:border-slate-800 dark:text-slate-400">
            {box.status === 'won' ? 'The deal is agreed, so this conversation is over.' : 'You opted out, so the agents will not write again.'}
            <Button icon={Plus} onClick={onNew}>Try again</Button>
          </div>
        ) : box.can?.start && !box.busy ? (
          <div className="border-t border-gray-100 p-3 dark:border-slate-800">
            <Button icon={RotateCcw} busy={sending === 'start'} onClick={() => act('start')}>Try the first email again</Button>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2 border-t border-gray-100 p-3 dark:border-slate-800">
              <textarea
                rows={3}
                className={`${inputCls} resize-none`}
                aria-label="Your reply"
                placeholder={box.busy ? 'The agents are working...' : `Reply as ${who}, the way an affiliate would`}
                value={input}
                disabled={!canType}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && input.trim() && canType) {
                    e.preventDefault();
                    act('reply');
                  }
                }}
              />
              <div className="flex flex-col gap-2">
                <Button variant="primary" icon={Send} busy={sending === 'reply'} disabled={!canType || !input.trim()} onClick={() => act('reply')}>Send</Button>
                <Button icon={Hourglass} busy={sending === 'no_reply'} disabled={!box.can?.no_reply || !!sending}
                  title="Pretend you stayed quiet: the team follows up as it would after its wait" onClick={() => act('no_reply')}>No reply</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 px-3 pb-3">
              {TRY_REPLIES.map((t) => {
                const value = t.replace('{domain}', box.persona.domain);
                return (
                  <button key={t} type="button" disabled={!canType} onClick={() => setInput(value)}
                    className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/40">
                    {value}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ---- a new sandbox ---- */

/** The deal desk, inline: describe a deal, answer its questions, save it. */
const DealDesk = ({ onSaved, onClose }) => {
  const [offer, setOffer] = useState(null);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(null);
  const [name, setName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const chatRef = useRef(null);
  const chat = offer?.chat || [];

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length, pending]);

  const send = async () => {
    const message = input.trim();
    if (!message || pending) return;
    setPending(message);
    setInput('');
    setError(null);
    try {
      const d = await api('/offers/chat', { method: 'POST', body: { offer_id: offer?.id, message } });
      // A yes in the chat saves the deal: use it straight away.
      if (d.saved) {
        onSaved(d.offer);
        return;
      }
      setOffer({ ...d.offer, ready: d.ready ?? d.offer.ready, missing: d.missing ?? d.offer.missing });
    } catch (err) {
      setError(err.message);
      setInput(message);
    } finally {
      setPending(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const d = await api(`/offers/${offer.id}/save`, { method: 'POST', body: { name: (name ?? '').trim() || undefined } });
      onSaved(d.offer);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const suggested = offer?.name && offer.name !== 'New deal' ? offer.name : '';
  return (
    <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-900">
      <div className="flex items-center justify-between gap-2 border-b border-blue-100 px-3 py-2 dark:border-blue-950">
        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-slate-100"><MessageSquare className="h-4 w-4" />Deal desk</span>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:underline dark:text-slate-400">Close</button>
      </div>
      <div ref={chatRef} className="max-h-80 space-y-2 overflow-y-auto bg-gray-50 p-3 dark:bg-slate-950">
        {!chat.length && !pending && (
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Explain the deal the way you would to a colleague: who it is for, the structure, where it opens and how far you can go. The deal desk asks about anything missing, then you save it.
          </p>
        )}
        {chat.map((m, i) => <Bubble key={`${i}-${m.at || ''}`} role={m.role} content={m.content} at={m.at} />)}
        {pending && <Bubble role="user" content={pending} waiting />}
        {pending && <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" />Deal desk is thinking...</div>}
      </div>
      {offer?.missing?.length > 0 && (
        <div className="border-t border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-950 dark:bg-amber-950/40 dark:text-amber-200">
          Still needed: {offer.missing.join('; ')}
        </div>
      )}
      {error && <div className="px-3 pt-2"><Notice tone="error">{error}</Notice></div>}
      {offer?.ready && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-3 py-2 dark:border-slate-800">
          <input className={`${inputCls} sm:w-64`} aria-label="Deal name" placeholder="Deal name" value={name ?? suggested} onChange={(e) => setName(e.target.value)} />
          <Button variant="primary" icon={Save} busy={saving} disabled={!!pending} onClick={save}>Save and use this deal</Button>
        </div>
      )}
      <div className="flex items-end gap-2 border-t border-gray-100 p-3 dark:border-slate-800">
        <textarea
          rows={2}
          className={`${inputCls} resize-none`}
          aria-label="Message to the deal desk"
          placeholder={offer ? 'Answer the deal desk' : 'e.g. 25% RevShare for UK casino sites, up to 35% for big ones'}
          value={input}
          disabled={!!pending}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button variant="primary" icon={Send} busy={!!pending} disabled={!input.trim()} onClick={send}>Send</Button>
      </div>
    </div>
  );
};

const NewSandbox = ({ meta, teams, defaultTeamId, marketName, onCreated, onTeamsChanged }) => {
  const [deals, setDeals] = useState([]);
  const [brands, setBrands] = useState([]);
  // Who approaches you: "t:<team id>" for a team, "b:<brand id>" for a brand that has none yet.
  const [form, setForm] = useState({
    target: defaultTeamId || teams[0] ? `t:${defaultTeamId || teams[0].id}` : '',
    offer_id: '', domain: '', name: '', email: '', note: '', geo: '', mailbox_id: '', lead: '', traffic: '', vet: true
  });
  const [desk, setDesk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadDeals = useCallback(() => {
    api('/offers?status=active').then((d) => setDeals(d.offers || [])).catch((err) => setError(err.message));
  }, []);
  useEffect(() => { loadDeals(); }, [loadDeals]);
  useEffect(() => {
    api('/brands').then((d) => {
      const list = d.brands || [];
      setBrands(list);
      // No team yet: start on the first dashboard brand.
      setForm((f) => (f.target || !list.length ? f : { ...f, target: `b:${(list.find((b) => b.kind === 'projects') || list[0]).id}` }));
    }).catch((err) => setError(err.message));
  }, []);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const team = form.target.startsWith('t:') ? teams.find((t) => `t:${t.id}` === form.target) || null : null;
  const brand = form.target.startsWith('b:') ? brands.find((b) => `b:${b.id}` === form.target) || null : null;
  const teamless = brands.filter((b) => !teams.some((t) => t.brand_id === b.id));
  const markets = team ? team.target_geos || [] : brand?.markets || [];
  const deal = deals.find((o) => String(o.id) === form.offer_id) || null;
  const teamDeals = deals.filter((o) => (team?.offer_ids || []).includes(o.id));
  const mode = (meta?.approval_modes || []).find((m) => m.key === team?.approval_mode);
  const senders = team?.senders || [];
  const domain = form.domain.trim().toLowerCase().replace(/^[a-z]+:\/\//, '').replace(/^www\./, '').split('/')[0];
  const local = form.name.trim().split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9._-]/g, '');
  const emailHint = domain ? `${local || 'partners'}@${domain}` : 'you@yoursite.com';

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await api('/sandbox', {
        method: 'POST',
        body: {
          team_id: team ? team.id : null,
          brand_id: brand ? brand.id : null,
          offer_id: form.offer_id ? Number(form.offer_id) : null,
          domain: form.domain,
          name: form.name,
          email: form.email,
          note: form.note,
          geo: form.geo || null,
          mailbox_id: form.mailbox_id ? Number(form.mailbox_id) : null,
          lead_with_offer: form.lead === '' ? null : form.lead === 'yes',
          traffic: form.traffic || null,
          vet: form.vet
        }
      });
      onCreated(d.sandbox);
      // A brand without a team just got one.
      if (brand && onTeamsChanged) onTeamsChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Notice tone="info">
        You play an affiliate and the real agents work on you. The Vetter checks your site, and the Contact agent writes to you. When you answer, the Reply reader sorts it, the Negotiator checks what you claim and plans the move inside the deal, and the Affiliate Manager writes it. Nothing is sent. Every email shows what live mode would do with it.
      </Notice>

      <Section title="1. The deal" hint="What the agents may offer you. Pick a saved deal, or explain a new one to the deal desk and answer its questions.">
        <Field label="Deal">
          <select className={inputCls} value={form.offer_id} onChange={set('offer_id')}>
            <option value="">The brand's own deal for my market{teamDeals.length ? ` (${teamDeals.map((o) => o.name).join(', ')})` : ''}</option>
            {deals.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}{o.traffic && o.traffic !== 'any' ? ` [${TRAFFIC_LABEL[o.traffic]}]` : ''}{o.opening_line ? `: opens at ${o.opening_line}` : ''}
              </option>
            ))}
          </select>
        </Field>
        {deal && (
          <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:bg-slate-950 dark:text-slate-300">
            {deal.summary || deal.opening_line}. {deal.lead_with_offer ? 'Named in the first email.' : 'Kept for the reply.'}
          </div>
        )}
        {!deal && (brand || (team && !teamDeals.length)) && (
          <div className="mt-2"><Notice tone="warn">{(team || brand).brand_name} has no saved deal. Pick one above or write one, or the Affiliate Manager will not put terms in front of you: any reply with numbers waits for a person.</Notice></div>
        )}
        {desk ? (
          <DealDesk
            onClose={() => setDesk(false)}
            onSaved={(o) => {
              setDesk(false);
              setDeals((list) => [o, ...list.filter((x) => x.id !== o.id)]);
              setForm((f) => ({ ...f, offer_id: String(o.id) }));
            }}
          />
        ) : (
          <div className="mt-3"><Button icon={MessageSquare} onClick={() => setDesk(true)}>Write a new deal with the deal desk</Button></div>
        )}
      </Section>

      <Section title="2. The brand that approaches you" hint="Any brand you added in MARS. A brand with a team brings its skills, lessons, mailboxes and approval settings.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Brand">
            <select className={inputCls} value={form.target} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, target: v, geo: '', mailbox_id: '' })); }}>
              {!teams.length && !brands.length && <option value="">No brands yet: add one in MARS first</option>}
              {teams.length > 0 && (
                <optgroup label="Brands with a team">
                  {teams.map((t) => <option key={t.id} value={`t:${t.id}`}>{t.brand_name} ({t.name})</option>)}
                </optgroup>
              )}
              {BRAND_KINDS.map((k) => {
                const list = teamless.filter((b) => b.kind === k.key);
                return list.length > 0 && (
                  <optgroup key={k.key} label={`${k.label}, no team yet`}>
                    {list.map((b) => <option key={b.id} value={`b:${b.id}`}>{b.brand_name} ({b.brand_url})</option>)}
                  </optgroup>
                );
              })}
            </select>
          </Field>
          <Field label="Your main market" hint="The Vetter can still pick another from your data.">
            <select className={inputCls} value={form.geo} onChange={set('geo')}>
              <option value="">Let the team decide</option>
              {markets.map((g) => <option key={g} value={g}>{marketName(g)}</option>)}
            </select>
          </Field>
          {senders.length > 1 && (
            <Field label="Mailbox that writes to you">
              <select className={inputCls} value={form.mailbox_id} onChange={set('mailbox_id')}>
                <option value="">The least used one, as in live</option>
                {senders.map((s) => <option key={s.mailbox_id} value={String(s.mailbox_id)}>{s.name ? `${s.name} <${s.email}>` : s.email}</option>)}
              </select>
            </Field>
          )}
        </div>
        {(team || brand) && (
          <div className="mt-3 space-y-0.5 text-xs text-gray-500 dark:text-slate-400">
            <div>
              Licences{deal?.licences?.length ? ' (from the deal)' : ''}: {(deal?.licences?.length ? deal.licences : team?.licences || []).join(', ') || 'none given yet'}
              {' '}· Markets (from the brand in MARS): {markets.map(marketName).join(', ') || 'none on the brand'}
            </div>
            {mode && <div>Approval: {mode.label}. {mode.hint}</div>}
          </div>
        )}
        {brand && (
          <div className="mt-3">
            <Notice tone="info">
              {brand.brand_name} has no team yet. Starting creates one for it, paused: it offers the deal you pick and writes from your first mailbox. It does nothing in live until you finish its setup and start it.
            </Notice>
          </div>
        )}
      </Section>

      <Section title="3. You, the affiliate" hint="The site you pretend to run. Pick a real affiliate site in MARS to see the Vetter judge it on its GEO and keyword data.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Your website">
            <input className={inputCls} placeholder="casinoguide.co.uk" value={form.domain} onChange={set('domain')} />
          </Field>
          <Field label="Your name" hint="Optional. The agents use your first name.">
            <input className={inputCls} placeholder="Lena Fox" value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Your email" hint="Optional. Nothing is sent to it.">
            <input className={inputCls} placeholder={emailHint} value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Name the deal in the first email">
            <select className={inputCls} value={form.lead} onChange={set('lead')}>
              <option value="">As the deal says</option>
              <option value="yes">Yes</option>
              <option value="no">No, keep it for the reply</option>
            </select>
          </Field>
          <Field label="Your traffic" hint="PPC affiliates pay for clicks and ask for more: they get the brand's PPC deal when it has one.">
            <select className={inputCls} value={form.traffic} onChange={set('traffic')}>
              <option value="">As MARS data shows for my site</option>
              <option value="seo">SEO: organic search</option>
              <option value="ppc">PPC: paid ads (Google, Meta ...)</option>
              <option value="mixed">Both SEO and PPC</option>
            </select>
          </Field>
          <Field label="What the team knows about you" hint="Optional, like the note on a hand-over. Every agent reads it." className="sm:col-span-2">
            <textarea rows={2} className={`${inputCls} resize-none`} placeholder="We met at ICE London; they run UK casino review pages." value={form.note} onChange={set('note')} />
          </Field>
        </div>
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-800 dark:text-slate-200">
          <input type="checkbox" className="mt-1" checked={form.vet} onChange={set('vet')} />
          <span>
            The Vetter checks my site first
            <span className="block text-xs text-gray-500 dark:text-slate-400">MARS rank data, the site itself and a short web search; its hook goes into the first email. Off: the Contact agent writes straight away.</span>
          </span>
        </label>
      </Section>

      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" icon={FlaskConical} busy={busy} disabled={!(team || brand) || !domain} onClick={start}>Let the agents approach me</Button>
        <span className="text-xs text-gray-500 dark:text-slate-400">The model calls are real, so each turn costs what a live one does and counts in the team's usage.</span>
      </div>
    </div>
  );
};

/* ---- the page ---- */

const SandboxItem = ({ s, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border p-3 text-left transition-colors ${selected
      ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700'}`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="min-w-0 truncate font-medium text-gray-900 dark:text-slate-100">{s.domain}</span>
      {s.busy ? <Loader2 className="h-4 w-4 flex-none animate-spin text-blue-600 dark:text-blue-400" /> : <SandboxStatus status={s.status} />}
    </div>
    <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-slate-400">{s.brand_name}{s.offer_name ? ` · ${s.offer_name}` : ''}</div>
    <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{s.emails} message{s.emails === 1 ? '' : 's'} · {timeAgo(s.updated_at)}</div>
  </button>
);

export default function Sandbox({ meta, teams, teamId, onTeamsChanged }) {
  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(null); // null: the form for a new one
  const [box, setBox] = useState(null);
  const [error, setError] = useState(null);
  const current = useRef(null); // the sandbox on screen; answers for any other are dropped

  const markets = meta?.markets || [];
  const marketName = (code) => markets.find((m) => m.code === code)?.name || code;

  const loadList = useCallback(() => {
    api('/sandbox').then((d) => setList(d.sandboxes || [])).catch((err) => setError(err.message));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const open = useCallback(async (id) => {
    current.current = id;
    setOpenId(id);
    setBox(null);
    setError(null);
    if (id == null) return;
    try {
      const d = await api(`/sandbox/${id}`);
      if (current.current === id) setBox(d.sandbox);
    } catch (err) {
      if (current.current === id) setError(err.message);
    }
  }, []);

  // While the agents work, watch them step by step.
  const busy = box?.busy || null;
  const boxId = box?.id || null;
  useEffect(() => {
    if (!busy || !boxId) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const d = await api(`/sandbox/${boxId}`);
        if (stopped || current.current !== boxId) return;
        setBox(d.sandbox);
        if (!d.sandbox.busy) loadList();
      } catch {
        // The next poll tries again.
      }
    }, 1500);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [busy, boxId, loadList]);

  const shown = (sb) => {
    current.current = sb.id;
    setOpenId(sb.id);
    setBox(sb);
    loadList();
  };
  const changed = (sb) => {
    if (current.current === sb.id) setBox(sb);
    loadList();
  };
  const remove = async (id) => {
    await api(`/sandbox/${id}`, { method: 'DELETE' });
    open(null);
    loadList();
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <aside className="space-y-3 lg:w-72 lg:flex-none">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-gray-600 dark:text-slate-400">Play an affiliate. Nothing is sent.</p>
          <Button variant="primary" icon={Plus} onClick={() => open(null)}>New</Button>
        </div>
        <div className="space-y-2 lg:max-h-[75vh] lg:overflow-y-auto">
          {!list.length && (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-slate-800 dark:text-slate-400">No sandbox chats yet.</div>
          )}
          {list.map((s) => <SandboxItem key={s.id} s={s} selected={s.id === openId} onClick={() => open(s.id)} />)}
        </div>
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
        {error && <Notice tone="error">{error}</Notice>}
        {openId == null ? (
          <NewSandbox meta={meta} teams={teams} defaultTeamId={teamId} marketName={marketName} onCreated={shown} onTeamsChanged={onTeamsChanged} />
        ) : box ? (
          <SandboxChat key={box.id} box={box} marketName={marketName} onChange={changed} onDelete={remove} onNew={() => open(null)} />
        ) : !error && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">Loading...</div>
        )}
      </div>
    </div>
  );
}
